import Color from "color";
import { t } from "ttag";
import _ from "underscore";

import { ColorSelector } from "metabase/common/components/ColorSelector";
import CS from "metabase/css/core/index.css";
import { Box, Button, Icon, NumberInput, Text } from "metabase/ui";
import { color } from "metabase/ui/colors";
import { getAccentColors } from "metabase/ui/colors/groups";
import type { ChartSettingSegmentsEditorProps } from "metabase/viz-core";
import type { ScalarSegment } from "metabase-types/api";

import { ChartSettingInput } from "../ChartSettingInput";

import S from "./ChartSettingSegmentsEditor.module.css";

export const ChartSettingSegmentsEditor = ({
  value: segments,
  onChange,
  canRemoveAll = false,
}: ChartSettingSegmentsEditorProps) => {
  const onChangeProperty = (
    index: number,
    property: keyof ScalarSegment,
    value: number | string | null,
  ) =>
    onChange([
      ...segments.slice(0, index),
      { ...segments[index], [property]: value },
      ...segments.slice(index + 1),
    ]);

  return (
    <Box px="1.5rem">
      {segments.length > 0 ? (
        <table className={S.Table}>
          <thead>
            <tr>
              <th />
              <th>{t`Label`}</th>
              <th>{t`Min`}</th>
              <th>{t`Max`}</th>
            </tr>
          </thead>
          <tbody>
            {segments.map((segment, index) => (
              <tr key={index}>
                <td>
                  <ColorSelector
                    pillSize="large"
                    className={S.ColorPill}
                    value={segment.color}
                    colors={getColorPalette()}
                    onChange={(color) =>
                      onChangeProperty(index, "color", color)
                    }
                  />
                </td>
                <td>
                  <ChartSettingInput
                    value={segment.label}
                    placeholder={t`Label for this range (optional)`}
                    onChange={(val) => onChangeProperty(index, "label", val)}
                  />
                </td>
                <td>
                  <NumberInput
                    className={CS.full}
                    value={segment.min ?? ""}
                    onBlur={(e) => {
                      const rawValue = e.target.value;
                      const newValue =
                        rawValue === "" ? null : parseFloat(rawValue);
                      if (newValue !== segment.min) {
                        onChangeProperty(index, "min", newValue);
                      }
                    }}
                    placeholder={t`Min`}
                    w="4rem"
                  />
                </td>
                <td>
                  <NumberInput
                    className={CS.full}
                    value={segment.max ?? ""}
                    onBlur={(e) => {
                      const rawValue = e.target.value;
                      const newValue =
                        rawValue === "" ? null : parseFloat(rawValue);
                      if (newValue !== segment.max) {
                        onChangeProperty(index, "max", newValue);
                      }
                    }}
                    placeholder={t`Max`}
                    w="4rem"
                  />
                </td>
                <td>
                  {/* TODO: replace with ActionIcon (GDGT-2457) */}
                  {(segments.length > 1 || canRemoveAll) && (
                    <Button
                      variant="subtle"
                      color="neutral"
                      size="sm"
                      leftSection={<Icon name="trash" />}
                      onClick={() =>
                        onChange(segments.filter((v, i) => i !== index))
                      }
                    />
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <Text
          ta="center"
          c="text-secondary"
          mt="2.5rem"
          mb="3rem"
          lh="1.25rem"
          px="1.5rem"
        >{t`Add color ranges to make this number change color depending on it's value`}</Text>
      )}
      <Button
        size="lg"
        leftSection={<Icon name="add" />}
        onClick={() => onChange(segments.concat(newSegment(segments)))}
        w="100%"
      >
        {t`Add a range`}
      </Button>
    </Box>
  );
};

export function getColorPalette() {
  return [
    ...getAccentColors(),
    Color(color("feedback-negative")).hex(),
    Color(color("feedback-warning")).hex(),
    Color(color("feedback-positive")).hex(),
  ];
}

function newSegment(segments: ScalarSegment[]) {
  const palette = getColorPalette();
  const lastSegment = segments[segments.length - 1];
  const lastMax =
    typeof lastSegment?.max === "number" && Number.isFinite(lastSegment.max)
      ? lastSegment.max
      : null;
  const lastColorIndex = lastSegment
    ? _.findIndex(palette, (color) => color === lastSegment.color)
    : -1;
  const nextColor =
    lastColorIndex >= 0
      ? palette[(lastColorIndex + 1) % palette.length]
      : palette[0];

  return {
    min: lastMax !== null ? lastMax : 0,
    max: lastMax !== null ? lastMax * 2 : 1,
    color: nextColor,
    label: "",
  };
}
